import { groupBy, orderBy } from 'lodash';
import {
  PropsWithChildren,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { GraphQLSubscriptionErrors } from '@/graphql/errors';
import { useRefetchOnAppResume } from '@/hooks/useRefetchOnAppResume';
import { useReportError } from '@/hooks/useReportError';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import {
  WorkflowTracker,
  WorkflowTrackerKind,
  WorkflowTrackerStatus,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import {
  EXCLUDED_WORKFLOW_TRACKER_KINDS,
  VISIBLE_WORKFLOW_TRACKER_KINDS,
} from '../constants';
import {
  useFetchHouseholdWorkflowTrackerHistoryQuery,
  useHouseholdWorkflowTrackerUpdatesSubscription,
} from '../graphql/HouseholdWorkflowTrackersContext.generated';
import { useRefetchActiveQueriesOnHouseholdWorkflowComplete } from '../hooks/useRefetchActiveQueriesOnHouseholdWorkflowComplete';
import {
  HouseholdWorkflowTrackersContext,
  type HouseholdWorkflowTrackersContextValue,
} from './HouseholdWorkflowTrackersContext.context';

const PAGE_SIZE = 50;
const STATUS_ORDER = {
  [WorkflowTrackerStatus.Queued]: 0,
  [WorkflowTrackerStatus.Started]: 1,
  [WorkflowTrackerStatus.Completed]: 2,
  [WorkflowTrackerStatus.Failed]: 2,
};

const EMPTY_VALUE: HouseholdWorkflowTrackersContextValue = {
  trackers: [],
  byId: {},
  awaitingTrackerCompletionByID: {},
  awaitingTrackerCompletionByKind: {},
  loading: true,
  refetch: async () => undefined,
};

interface HouseholdWorkflowTrackersProviderProps extends PropsWithChildren {
  householdID: string | null;
}

function HouseholdWorkflowQueryRefetcher() {
  useRefetchActiveQueriesOnHouseholdWorkflowComplete();
  return null;
}

function useContextValue(
  householdID: string | null,
  userID: string | undefined
): HouseholdWorkflowTrackersContextValue {
  const { reportError } = useReportError();

  // The keyed operation owner has one household/user lifetime. Apollo can use
  // current callbacks, so a render-time token alone cannot isolate operations.
  const active = useRef(true);
  useLayoutEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  // Live cache of household workflow trackers
  const [snapshot, setSnapshot] = useState<{
    trackersById: Record<string, WorkflowTracker>;
    awaitingTrackerCompletionByID: Record<string, WorkflowTrackerKind>;
    snapshotStatus?: HouseholdWorkflowTrackersContextValue['snapshotStatus'];
  }>({ trackersById: {}, awaitingTrackerCompletionByID: {} });
  const { trackersById, awaitingTrackerCompletionByID, snapshotStatus } =
    snapshot;

  function mergeTrackers(list: WorkflowTracker[], fromQuery: boolean) {
    if (!householdID || !userID) return;
    setSnapshot((previous) => {
      if (!active.current) return previous;
      // The query returns only the first page, not the full tracker history.
      // Omission must not remove a known tracker or imply completion.
      const merged = { ...previous.trackersById };
      const awaiting = { ...previous.awaitingTrackerCompletionByID };
      for (const tracker of list) {
        if (tracker.householdID && tracker.householdID !== householdID)
          continue;
        if (EXCLUDED_WORKFLOW_TRACKER_KINDS.includes(tracker.kind)) continue;
        const known = merged[tracker.id];
        if (known && known.updatedAt > tracker.updatedAt) continue;
        // Date loses sub-millisecond precision. On a tie, do not move backwards
        // in the lifecycle. Equal-rank metadata (including terminal status) uses
        // the last arrival; a strictly newer timestamp always wins.
        if (
          known &&
          +known.updatedAt === +tracker.updatedAt &&
          STATUS_ORDER[known.status] > STATUS_ORDER[tracker.status]
        )
          continue;
        merged[tracker.id] = tracker;
        // Preserve the existing subscription contract for household-less and
        // Queued events. Query snapshots reconcile every returned status.
        if (!fromQuery && !tracker.householdID) continue;
        if (tracker.status === WorkflowTrackerStatus.Started) {
          awaiting[tracker.id] = tracker.kind;
        } else if (
          fromQuery ||
          tracker.status === WorkflowTrackerStatus.Completed ||
          tracker.status === WorkflowTrackerStatus.Failed
        ) {
          delete awaiting[tracker.id];
        }
      }
      let nextStatus = previous.snapshotStatus;
      if (fromQuery) {
        nextStatus = { householdID, unavailable: false };
      }
      return {
        trackersById: merged,
        awaitingTrackerCompletionByID: awaiting,
        snapshotStatus: nextStatus,
      };
    });
  }

  const { loading, refetch } = useFetchHouseholdWorkflowTrackerHistoryQuery({
    skip: !userID || !householdID,
    variables: {
      householdID: householdID ?? '',
      first: PAGE_SIZE,
      kinds: VISIBLE_WORKFLOW_TRACKER_KINDS,
    },
    // This provider owns its snapshot. Do not reuse data or an in-flight request
    // from an earlier user/household lifetime, even on A -> B -> A navigation.
    fetchPolicy: 'no-cache',
    context: { queryDeduplication: false },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (!active.current || !householdID || !userID) return;
      mergeTrackers(
        getNodes(data.householdWorkflowTrackerHistory) as WorkflowTracker[],
        true
      );
    },
    onError: (error) => {
      if (!active.current || !householdID || !userID) return;
      setSnapshot((prev) => {
        if (!active.current) return prev;
        return {
          ...prev,
          snapshotStatus: { householdID, unavailable: true },
        };
      });
      reportError(
        'Failed to fetch household workflow trackers',
        error as Error
      );
    },
  });

  // SUBSCRIBE TO HOUSEHOLD WORKFLOW TRACKER UPDATES
  const { restart } = useHouseholdWorkflowTrackerUpdatesSubscription({
    skip: !userID || !householdID,
    fetchPolicy: 'no-cache',
    context: { queryDeduplication: false },
    variables: { householdID: householdID ?? '' },
    onData: ({ data }) => {
      if (!active.current || !householdID || !userID) return;
      if (!data.data?.householdWorkflowTrackerUpdates) return;

      const tracker = data.data
        .householdWorkflowTrackerUpdates as WorkflowTracker;

      if (tracker.householdID && tracker.householdID !== householdID) return;
      if (EXCLUDED_WORKFLOW_TRACKER_KINDS.includes(tracker.kind)) return;

      diagnostics.debug(
        '[HouseholdWorkflowTrackersProvider] New household tracker update received',
        {
          trackerId: tracker.id,
          trackerKind: tracker.kind,
          trackerStatus: tracker.status,
          householdID: tracker.householdID,
          createdAt: tracker.createdAt,
        }
      );

      mergeTrackers([tracker], false);
    },
    onError: (error) => {
      if (!active.current) return;
      const errorMessage = error.message || JSON.stringify(error);

      if (errorMessage.includes(GraphQLSubscriptionErrors.RECONNECT_MESSAGE)) {
        diagnostics.debug(
          'Household workflow tracker updates subscription reconnecting, refetching household workflow trackers'
        );
        // restart the subscription to ensure we don't miss any workflow trackers
        restart();
        // refetch to ensure we have the latest workflow trackers
        void refetch();
      } else {
        diagnostics.error(
          'Failed to subscribe to household workflow tracker updates',
          error
        );
      }
    },
  });

  // REFETCH ON APP RESUME
  useRefetchOnAppResume(
    () => {
      if (!active.current || !householdID || !userID) return;
      restart();
      return refetch();
    },
    {
      label: `HouseholdWorkflowTrackers:${householdID ?? 'none'}`,
      enabled: !!userID && !!householdID,
    }
  );

  // DERIVED STATE VARIABLES

  const trackers = useMemo(
    () =>
      orderBy(
        Object.values(trackersById),
        'createdAt',
        'desc'
      ) as WorkflowTracker[],
    [trackersById]
  );

  const awaitingTrackerCompletionByKind = useMemo(() => {
    return groupBy(
      Object.keys(awaitingTrackerCompletionByID),
      (id) => awaitingTrackerCompletionByID[id]
    );
  }, [awaitingTrackerCompletionByID]);

  return useMemo(
    () => ({
      trackers,
      byId: trackersById,
      refetch,
      loading,
      snapshotStatus,
      awaitingTrackerCompletionByID,
      awaitingTrackerCompletionByKind,
    }),
    [
      trackers,
      trackersById,
      refetch,
      loading,
      snapshotStatus,
      awaitingTrackerCompletionByID,
      awaitingTrackerCompletionByKind,
    ]
  );
}

function HouseholdTrackerOperations({
  householdID,
  userID,
  onChange,
}: {
  householdID: string | null;
  userID: string | undefined;
  onChange: (value: HouseholdWorkflowTrackersContextValue) => void;
}) {
  const value = useContextValue(householdID, userID);
  useLayoutEffect(() => onChange(value), [onChange, value]);
  return null;
}

export function HouseholdWorkflowTrackersProvider({
  children,
  householdID,
}: HouseholdWorkflowTrackersProviderProps) {
  const userID = useCurrentUser()?.id;
  const scope = useMemo(() => ({ householdID, userID }), [householdID, userID]);
  const [published, setPublished] = useState<{
    scope: typeof scope;
    value: HouseholdWorkflowTrackersContextValue;
  }>();
  const onChange = useCallback(
    (value: HouseholdWorkflowTrackersContextValue) => {
      setPublished({ scope, value });
    },
    [scope]
  );
  const value = published?.scope === scope ? published.value : EMPTY_VALUE;
  return (
    <HouseholdWorkflowTrackersContext.Provider value={value}>
      {/* Only operations remount. Consumer forms and other UI retain their state. */}
      <HouseholdTrackerOperations
        key={JSON.stringify([householdID, userID])}
        householdID={householdID}
        userID={userID}
        onChange={onChange}
      />
      <HouseholdWorkflowQueryRefetcher
        key={JSON.stringify(['workflow-completion', householdID, userID])}
      />
      {children}
    </HouseholdWorkflowTrackersContext.Provider>
  );
}
